/** Shared helpers for Format Builder UI — keep tabs crash-proof against odd API shapes. */

/**
 * Built-in formats use { name, label, left, top, width, height }.
 * Custom / RegionEditor use { region_name, region_label, left_percent, ... }.
 * Normalize to the editor shape so labels and boxes actually show.
 */
export function normalizeRegionsForEditor(regions) {
  if (!Array.isArray(regions)) return [];
  return ensureClientRegionIds(regions.map((r) => ({
    ...r,
    region_name: r.region_name || r.name || '',
    region_label: r.region_label || r.label || '',
    type: r.type || 'table',
    left_percent: numOr(r.left_percent, r.left, 0),
    top_percent: numOr(r.top_percent, r.top, 0),
    width_percent: numOr(r.width_percent, r.width, 0),
    height_percent: numOr(r.height_percent, r.height, 0),
  })));
}

/** Convert editor regions → extractable / override shape used by formats.js + formatOverrides. */
export function regionsForOverrideSave(regions) {
  return normalizeRegionsForEditor(regions).map((r) => ({
    id: r.id,
    name: r.region_name || r.name || `region_${r.id}`,
    label: r.region_label || r.label || `Region ${r.id}`,
    type: r.type || 'table',
    left: r.left_percent,
    top: r.top_percent,
    width: r.width_percent,
    height: r.height_percent,
  }));
}

function numOr(...vals) {
  for (const v of vals) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/** Assign stable numeric ids so Columns / AI Prompts dropdowns can bind to region_id. */
export function ensureClientRegionIds(regions) {
  if (!Array.isArray(regions)) return [];
  const used = new Set();
  const staged = regions.map((r) => {
    const id = r.id != null ? parseInt(r.id, 10) : NaN;
    if (Number.isFinite(id) && id >= 1 && !used.has(id)) {
      used.add(id);
      return { ...r, id };
    }
    return { ...r, id: null };
  });
  let nextId = 1;
  return staged.map((r) => {
    if (r.id != null) return r;
    while (used.has(nextId)) nextId++;
    const id = nextId++;
    used.add(id);
    return { ...r, id };
  });
}

/** Backend may return prompts as an object keyed by region_id; UI always uses an array. */
export function normalizePrompts(prompts) {
  if (Array.isArray(prompts)) return prompts;
  if (!prompts || typeof prompts !== 'object') return [];
  return Object.entries(prompts).map(([rid, val]) => {
    if (typeof val === 'string') {
      return {
        region_id: parseInt(rid, 10),
        prompt_template: val,
        ai_model: 'gpt-4o',
        use_auto_format: true,
      };
    }
    return {
      region_id: val.region_id != null ? val.region_id : parseInt(rid, 10),
      prompt_template: val.prompt_template || val.template || '',
      ai_model: val.ai_model || 'gpt-4o',
      use_auto_format: val.use_auto_format !== false,
    };
  });
}

/** Group formats for <select> optgroups by engineering firm. */
export function groupFormatsByFirm(formats) {
  const groups = new Map();
  for (const f of formats || []) {
    const firm = (f.engineeringFirm || f.engineering_firm || '').trim() || 'Unassigned firm';
    if (!groups.has(firm)) groups.set(firm, []);
    groups.get(firm).push(f);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a === 'Unassigned firm') return 1;
      if (b === 'Unassigned firm') return -1;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    })
    .map(([firm, items]) => ({
      firm,
      formats: items.slice().sort((x, y) => (x.name || '').localeCompare(y.name || '', undefined, { sensitivity: 'base' })),
    }));
}
